import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Check,
  Copy,
  ExternalLink,
  Megaphone,
  Package,
  PackageOpen,
  Palette,
  Phone,
  Share2,
  Store,
  Tag,
} from "lucide-react";
import {
  STORE_BANNER_BGS,
  STORE_BANNER_BG_KEYS,
  STORE_CURRENCY,
  STORE_HERO_BGS,
  contentFor,
  formatPrice,
  storePath,
  storePriceOf,
  type StoreBannerBg,
  type StoreInput,
} from "@nh/shared";
import { PageHeader } from "@/shared/ui/layout";
import { Alert, Button, Checkbox, Input, Textarea } from "@/shared/ui/core";
import { EmptyState } from "@/shared/ui/ui";
import { DataTable, DataTableFrame, KpiCard, Td, Th, Tr } from "@/features/admin/components";
import { InviteQr } from "@/features/network/InviteQr";
import { useMyStore, useUpdateMyStore } from "./minegocio.api";

/**
 * ═══════════════════════════════════════════════════════════════════
 * Mi tienda — la LÓGICA del sistema viejo (`dashboard/mi-tienda`),
 * portada al DS (Anahí, 27-ago): "Tú decides qué productos aparecen,
 * a qué precio y quién entra."
 *
 * En orden, como allá:
 *  1. COMPARTIR el enlace (lo de todos los días va primero).
 *  2. El RESUMEN en números.
 *  3. IDENTIDAD — nombre, enlace, color de marca y bienvenida.
 *  4. EXPERIENCIA — banner con su fondo, anuncio, teléfono, paquetes.
 *  5. PRODUCTOS Y PRECIOS — apagar lo que no vende y fijar su precio;
 *     vacío = el precio de socio del catálogo.
 *
 * TODO se guarda con un solo botón: la pantalla es un formulario, no
 * diez. El inventario sigue siendo derivado — se compra, no se escribe.
 * ═══════════════════════════════════════════════════════════════════
 */
/** Paletas listas para la marca: el violeta de la casa y compañía. */
const BRAND_COLOR_PRESETS = [
  "#5C4FD6",
  "#EC4899",
  "#F59E0B",
  "#10B981",
  "#0EA5E9",
  "#1F2430",
] as const;

export function TiendaPage() {
  const { t } = useTranslation();
  const store = useMyStore();
  const update = useUpdateMyStore();

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [showPackages, setShowPackages] = useState(false);
  const [brandColor, setBrandColor] = useState("#5C4FD6");
  const [welcome, setWelcome] = useState("");
  const [bannerMessage, setBannerMessage] = useState("");
  const [bannerBg, setBannerBg] = useState<StoreBannerBg>("violeta");
  const [announcement, setAnnouncement] = useState("");
  const [phone, setPhone] = useState("");
  const [heroBg, setHeroBg] = useState<StoreBannerBg>("tinta");
  const [heroItems, setHeroItems] = useState<string[]>([]);
  const [disabled, setDisabled] = useState<Set<string>>(new Set());
  /** Precio propio por producto, EN PESOS como texto ("" = catálogo). */
  const [ownPrices, setOwnPrices] = useState<Record<string, string>>({});
  /*
   * Cuántas piezas publica de cada producto. Se guarda como TEXTO y no
   * como número por lo mismo que los precios: mientras se escribe, la
   * caja pasa por estados que no son un número —vacía, "0", un pegado a
   * medias— y convertir en cada tecla le arrebata el cursor a quien
   * teclea. La conversión ocurre una sola vez, al guardar.
   */
  const [stockCaps, setStockCaps] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!store.data) return;
    const s = store.data.store;
    setName(s.name);
    setSlug(s.slug);
    setShowPackages(s.showPackages);
    setBrandColor(s.branding.brandColor);
    setWelcome(s.branding.welcome);
    setBannerMessage(s.branding.bannerMessage);
    setBannerBg(s.branding.bannerBg);
    setAnnouncement(s.branding.announcement);
    setPhone(s.branding.phone);
    setHeroBg(s.branding.heroBg);
    setHeroItems(s.branding.heroProductIds);
    setDisabled(
      new Set(store.data.items.filter((item) => !item.enabled).map((item) => item.product.id)),
    );
    /*
     * El tope solo se siembra cuando DIFIERE de lo que tiene: así la
     * caja sale vacía —"publico todo"— para quien nunca lo tocó, y con
     * número solo para quien decidió guardarse piezas.
     */
    setStockCaps(
      Object.fromEntries(
        store.data.items
          .filter((item) => item.stock !== item.stockOwned)
          .map((item) => [item.product.id, String(item.stock)]),
      ),
    );
    setOwnPrices(
      Object.fromEntries(
        store.data.items
          .filter((item) => {
            const base = storePriceOf(item.product, STORE_CURRENCY);
            return item.priceCents !== null && item.priceCents !== (base ?? null);
          })
          .map((item) => [item.product.id, String((item.priceCents as number) / 100)]),
      ),
    );
  }, [store.data]);

  const items = store.data?.items ?? [];
  const slugLocked = store.data?.store.slugLocked ?? false;
  const url = slug ? `${window.location.origin}${storePath(slug)}` : "";

  const visibles = items.filter((item) => !disabled.has(item.product.id));
  // El KPI cuenta lo que TIENE, no lo que publica: es "unidades de tus
  // paquetes", y guardarse piezas no las hace desaparecer del almacén.
  const piezas = items.reduce((sum, item) => sum + item.stockOwned, 0);
  const conPrecio = useMemo(
    () => Object.values(ownPrices).filter((raw) => Number(raw) > 0).length,
    [ownPrices],
  );

  function toggle(productId: string) {
    setDisabled((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  }

  async function guardar() {
    setSaved(false);
    const pricing: StoreInput["pricing"] = {};
    for (const [productId, raw] of Object.entries(ownPrices)) {
      const pesos = Number(raw);
      if (Number.isFinite(pesos) && pesos >= 1) pricing[productId] = Math.round(pesos * 100);
    }
    /*
     * Los topes de stock. Una caja VACÍA no es un tope de cero: es "no
     * tengo tope, publica todo lo que tenga" — que es distinto, y la
     * diferencia importa el día que le lleguen más piezas. El cero se
     * escribe a mano y sí significa "no lo enseño".
     */
    const caps: StoreInput["stockCaps"] = {};
    for (const [productId, raw] of Object.entries(stockCaps)) {
      if (raw.trim() === "") continue;
      const piezas = Number(raw);
      if (Number.isInteger(piezas) && piezas >= 0) caps[productId] = piezas;
    }
    await update.mutateAsync({
      name: name.trim(),
      slug: slug.trim(),
      showPackages,
      branding: {
        brandColor,
        welcome: welcome.trim(),
        bannerMessage: bannerMessage.trim(),
        bannerBg,
        announcement: announcement.trim(),
        phone: phone.trim(),
        heroBg,
        heroProductIds: heroItems,
      },
      disabledProductIds: [...disabled],
      pricing,
      stockCaps: caps,
    });
    setSaved(true);
  }

  return (
    <>
      <PageHeader
        titleHidden
        title={t("Mi tienda")}
        description={t(
          "Tú decides qué productos aparecen, a qué precio, y cómo se ve tu tienda.",
        )}
      />

      <div className="grid gap-4">
        {/* ── 1 · El resumen en números, arriba de todo ────────────────────────────────── */}
        <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(170px,1fr))]">
          <KpiCard
            icon={Store}
            item="tienda"
            label={t("En tu tienda")}
            value={String(visibles.length)}
            hint={t("de {{n}} en tu inventario", { n: items.length })}
          />
          <KpiCard icon={Package} item="box" label={t("Piezas en stock")} value={String(piezas)} />
          <KpiCard icon={Tag} label={t("Con precio propio")} value={String(conPrecio)} />
          <KpiCard
            icon={Megaphone}
            label={t("Compra de paquetes")}
            value={showPackages ? t("Visible") : t("Apagada")}
          />
        </div>

        {/* ── 2 · Comparte tu tienda ──── */}
        <section className="relative rounded-3xl border border-ds-border bg-white p-5 shadow-ds-float">
          <div className="relative flex flex-wrap items-center justify-between gap-3">
            <h2 className="flex items-center gap-2 text-[15px] font-semibold text-ds-ink">
              <Share2 className="h-4 w-4 text-ds-violet-pop" />
              {t("Comparte tu tienda")}
            </h2>
            {store.data && (
              <Button
                variant="secondary"
                size="sm"
                as={Link}
                to={storePath(store.data.store.slug)}
                leftIcon={<ExternalLink />}
              >
                {t("Ver mi tienda")}
              </Button>
            )}
          </div>
          <p className="relative mt-1 text-[13px] text-ds-ink-3">
            {t(
              "Este es el enlace para tus clientes: quien entre ve tus productos a tus precios.",
            )}
          </p>
          <div className="relative mt-3 flex flex-wrap items-center gap-2">
            <span className="min-w-0 flex-1 truncate rounded-xl border border-ds-border bg-white px-3 py-2 font-mono text-[13px] text-ds-ink">
              {url || t("Guarda tu enlace para compartirlo")}
            </span>
            <Button
              variant="secondary"
              size="sm"
              leftIcon={copied ? <Check /> : <Copy />}
              disabled={!url}
              onClick={() => {
                void navigator.clipboard?.writeText(url);
                setCopied(true);
                window.setTimeout(() => setCopied(false), 2000);
              }}
            >
              {copied ? t("Copiado") : t("Copiar")}
            </Button>
            <Button
              size="sm"
              nav
              disabled={!url}
              onClick={() =>
                window.open(
                  `https://wa.me/?text=${encodeURIComponent(
                    t("Te comparto mi tienda de It's The Best: {{url}}", { url }),
                  )}`,
                  "_blank",
                  "noreferrer",
                )
              }
            >
              {t("Compartir por WhatsApp")}
            </Button>
          </div>
          {url && (
            <div className="relative mt-4">
              <InviteQr
                url={url}
                fileName="mi-tienda-its-the-best"
                shareText={t("Te comparto mi tienda de It's The Best:")}
              />
            </div>
          )}
        </section>

        {/* ── 3 · Identidad ────────────────────────────────────────────── */}
        <section className="relative rounded-3xl border border-ds-border bg-white p-5 shadow-ds-float">
          <h2 className="relative flex items-center gap-2 text-[15px] font-semibold text-ds-ink">
            <Palette className="h-4 w-4 text-ds-violet-pop" />
            {t("Identidad de tu tienda")}
          </h2>
          <div className="relative mt-4 grid gap-4 lg:grid-cols-2">
            <label className="grid gap-1 text-[12px] font-medium text-ds-ink-3">
              {t("Nombre de tu tienda")}
              <Input
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Annieshop"
              />
            </label>

            <label className="grid gap-1 text-[12px] font-medium text-ds-ink-3">
              {t("Tu enlace")}
              {/*
                UNA sola personalización (Anahí, 27-ago): la tienda nace
                con un enlace derivado, se elige el definitivo con calma
                una vez, y queda fijo — el servidor lo exige igual.
              */}
              <Input
                value={slug}
                disabled={slugLocked}
                onChange={(event) =>
                  setSlug(event.target.value.toLowerCase().replace(/\s+/g, "-"))
                }
                placeholder="annie"
              />
              <span className="text-[11px] text-ds-ink-3">
                {slugLocked
                  ? t("Tu enlace ya quedó fijo: se personaliza una sola vez.")
                  : t("Puedes cambiarlo UNA sola vez. Elígelo con calma: después queda fijo para que tus clientes nunca lleguen a rutas muertas.")}
              </span>
            </label>

            <label className="grid gap-1 text-[12px] font-medium text-ds-ink-3 lg:col-span-2">
              {t("Bienvenida")}
              <Input
                value={welcome}
                onChange={(event) => setWelcome(event.target.value)}
                placeholder={t("Bienestar que se nota, de mi familia a la tuya.")}
              />
            </label>

            {/*
              El COLOR DE MARCA con su vista previa (Anahí, 27-ago: "se ve
              muy pequeño e indica en qué parte de la página tendrá
              efecto"): paletas listas + el gotero libre a la izquierda,
              y a la derecha una MINI VITRINA que pinta en vivo lo que el
              color toca de verdad — el nombre, el precio, el botón y el
              botón de WhatsApp.
            */}
            <div className="grid gap-1 text-[12px] font-medium text-ds-ink-3 lg:col-span-2">
              {t("Color de tu marca")}
              <div className="mt-1 grid gap-4 rounded-2xl border border-ds-border bg-ds-mist/20 p-4 sm:grid-cols-[1fr_260px]">
                <div className="grid content-start gap-3">
                  <p className="text-[12.5px] font-normal leading-relaxed text-ds-ink-3">
                    {t(
                      "Pinta tu vitrina entera: el nombre en la portada, los precios, los botones de comprar y el de WhatsApp.",
                    )}
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    {BRAND_COLOR_PRESETS.map((preset) => (
                      <button
                        key={preset}
                        type="button"
                        aria-label={t("Color {{hex}}", { hex: preset })}
                        aria-pressed={brandColor.toLowerCase() === preset.toLowerCase()}
                        onClick={() => setBrandColor(preset)}
                        className={`h-9 w-9 rounded-full ring-offset-2 transition ${
                          brandColor.toLowerCase() === preset.toLowerCase()
                            ? "ring-2 ring-ds-ink"
                            : "hover:ring-1 hover:ring-ds-border-strong"
                        }`}
                        style={{ background: preset }}
                      />
                    ))}
                    {/* El gotero libre, del mismo tamaño que las paletas. */}
                    <span className="relative inline-flex h-9 w-9 overflow-hidden rounded-full ring-1 ring-ds-border">
                      <input
                        type="color"
                        aria-label={t("Elegir otro color")}
                        className="absolute -inset-2 h-[52px] w-[52px] cursor-pointer border-0 p-0"
                        value={brandColor}
                        onChange={(event) => setBrandColor(event.target.value)}
                      />
                    </span>
                    <span className="ml-1 rounded-lg bg-white px-2.5 py-1.5 font-mono text-[12px] text-ds-ink ring-1 ring-ds-border">
                      {brandColor.toUpperCase()}
                    </span>
                  </div>
                </div>

                {/* ── La mini vitrina: lo que el color toca, en vivo ── */}
                <div className="rounded-2xl border border-ds-border bg-white p-4 shadow-ds-card">
                  <p className="text-[10px] font-bold uppercase tracking-ds-label text-ds-ink-4">
                    {t("Así se ve en tu tienda")}
                  </p>
                  <p className="mt-1.5 text-[15px] font-light text-ds-ink">
                    {t("Bienvenido a")}{" "}
                    <span className="font-semibold" style={{ color: brandColor }}>
                      {name || "Annieshop"}
                    </span>
                  </p>
                  <p className="mt-1 text-[15px] font-semibold" style={{ color: brandColor }}>
                    $799.00{" "}
                    <span className="text-[11px] font-normal text-ds-ink-3 line-through">
                      $1,630.00
                    </span>
                  </p>
                  <div className="mt-2.5 flex flex-wrap gap-1.5">
                    <span
                      className="inline-flex items-center rounded-full px-3.5 py-1.5 text-[11px] font-semibold text-white"
                      style={{ background: brandColor }}
                    >
                      {t("Ver producto")}
                    </span>
                    <span
                      className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-[11px] font-semibold text-white"
                      style={{ background: brandColor }}
                    >
                      <Phone className="h-3 w-3" />
                      {t("Escríbeme")}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── 4 · Experiencia — el banner, el anuncio y el contacto ────── */}
        <section className="relative rounded-3xl border border-ds-border bg-white p-5 shadow-ds-float">
          <h2 className="relative flex items-center gap-2 text-[15px] font-semibold text-ds-ink">
            <Store className="h-4 w-4 text-ds-violet-pop" />
            {t("Experiencia de tu tienda")}
          </h2>

          {/* La vista previa: el banner tal cual quedará, antes de guardar. */}
          <div
            className="relative mt-4 flex min-h-[72px] items-center rounded-2xl px-5 py-4 text-white"
            style={{ background: STORE_BANNER_BGS[bannerBg] }}
          >
            <span className="text-[15px] font-semibold drop-shadow">
              {bannerMessage || t("Tu mensaje del banner aquí")}
            </span>
          </div>

          <div className="relative mt-4 grid gap-4 lg:grid-cols-2">
            <label className="grid gap-1 text-[12px] font-medium text-ds-ink-3">
              {t("Mensaje del banner")}
              <Input
                value={bannerMessage}
                onChange={(event) => setBannerMessage(event.target.value)}
                placeholder={t("Bienestar que se nota")}
              />
            </label>

            <div className="grid gap-1 text-[12px] font-medium text-ds-ink-3">
              {t("Fondo del banner")}
              <div className="flex flex-wrap items-center gap-2 pt-1">
                {STORE_BANNER_BG_KEYS.map((key) => (
                  <button
                    key={key}
                    type="button"
                    aria-label={t("Fondo {{name}}", { name: key })}
                    aria-pressed={bannerBg === key}
                    onClick={() => setBannerBg(key)}
                    className={`h-8 w-8 rounded-full ring-offset-2 transition ${
                      bannerBg === key
                        ? "ring-2 ring-ds-violet-pop"
                        : "hover:ring-1 hover:ring-ds-border"
                    }`}
                    style={{ background: STORE_BANNER_BGS[key] }}
                  />
                ))}
              </div>
            </div>

            <label className="grid gap-1 text-[12px] font-medium text-ds-ink-3 lg:col-span-2">
              {t("Anuncio")}
              <Textarea
                rows={2}
                value={announcement}
                onChange={(event) => setAnnouncement(event.target.value)}
                placeholder={t("Tu próxima presentación o cualquier aviso para quien entre.")}
              />
            </label>

            <label className="grid gap-1 text-[12px] font-medium text-ds-ink-3">
              {t("WhatsApp de contacto")}
              <Input
                inputMode="tel"
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
                placeholder="55 1234 5678"
              />
              <span className="text-[11px] text-ds-ink-3">
                {t("Con número, tu tienda enseña un botón de “Escríbeme”.")}
              </span>
            </label>

            {/* ── La PORTADA: su tinta y sus productos flotando ────── */}
            <div className="grid gap-1 text-[12px] font-medium text-ds-ink-3">
              {t("Fondo de la portada")}
              <div className="flex flex-wrap items-center gap-2 pt-1">
                {STORE_BANNER_BG_KEYS.map((key) => (
                  <button
                    key={key}
                    type="button"
                    aria-label={t("Portada {{name}}", { name: key })}
                    aria-pressed={heroBg === key}
                    onClick={() => setHeroBg(key)}
                    className={`h-8 w-8 rounded-full ring-offset-2 transition ${
                      heroBg === key
                        ? "ring-2 ring-ds-violet-pop"
                        : "hover:ring-1 hover:ring-ds-border"
                    }`}
                    style={{ background: STORE_HERO_BGS[key] }}
                  />
                ))}
              </div>
            </div>

            <div className="grid gap-1 text-[12px] font-medium text-ds-ink-3">
              {t("Productos que flotan en tu portada (hasta 3)")}
              <div className="flex flex-wrap gap-2 pt-1">
                {items.map(({ product }) => {
                  const elegido = heroItems.includes(product.id);
                  return (
                    <button
                      key={product.id}
                      type="button"
                      aria-pressed={elegido}
                      onClick={() =>
                        setHeroItems((prev) =>
                          elegido
                            ? prev.filter((id) => id !== product.id)
                            : prev.length >= 3
                              ? prev
                              : [...prev, product.id],
                        )
                      }
                      className={`rounded-full px-3.5 py-1.5 text-[12px] font-semibold transition ${
                        elegido
                          ? "bg-ds-violet-pop text-white"
                          : "border border-ds-border bg-white text-ds-ink-3 hover:text-ds-ink"
                      }`}
                    >
                      {contentFor(product.translations, "es").name}
                    </button>
                  );
                })}
                {items.length === 0 && (
                  <span className="text-[12px] font-normal text-ds-ink-3">
                    {t("Con inventario, aquí eliges cuáles se lucen.")}
                  </span>
                )}
              </div>
              <span className="text-[11px] font-normal text-ds-ink-3">
                {t("Sin elegir, la portada usa los primeros de tu vitrina.")}
              </span>
            </div>

            <Checkbox
              label={t("Mostrar compra de paquetes en mi tienda")}
              hint={t(
                "Si habilitas la compra de paquetes ganas los bonos del plan de referidos de It's The Best, no del sobreprecio de ningún paquete.",
              )}
              checked={showPackages}
              onChange={(event) => setShowPackages(event.target.checked)}
            />
          </div>
        </section>

        {/* ── 5 · Productos y precios ──────────────────────────────────── */}
        <DataTableFrame>
          <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-ds-border px-5 py-3.5">
            <h2 className="text-[15px] font-semibold text-ds-ink">
              {t("Productos y precios")}
            </h2>
            <span className="text-[12px] font-medium text-ds-ink-3">
              {t("{{n}} de {{m}} visibles", { n: visibles.length, m: items.length })}
            </span>
          </div>
          <p className="border-b border-ds-border px-5 py-3 text-[12.5px] leading-relaxed text-ds-ink-3">
            {t(
              "Aquí aparece solo lo que tus paquetes te han surtido: tu tienda vende lo que compraste. Apaga lo que no quieras vender, y deja el precio vacío para usar el de socio del catálogo.",
            )}
          </p>
          {items.length === 0 ? (
            <div className="p-4">
              <EmptyState
                icon={<PackageOpen className="h-5 w-5 text-ds-ink-3" />}
                title={t("Sin productos que vender")}
                description={t(
                  "Tu inventario sale de tus paquetes mayoristas: sin paquete no hay producto que vender. Adquiere uno para llenar tu tienda.",
                )}
              />
            </div>
          ) : (
            <DataTable caption={t("Lo que vende tu tienda")}>
              <thead>
                <tr>
                  <Th>{t("Visible")}</Th>
                  <Th>{t("Producto")}</Th>
                  <Th align="right">{t("Publicas")}</Th>
                  <Th align="right">{t("Precio de catálogo")}</Th>
                  <Th align="right">{t("Tu precio")}</Th>
                </tr>
              </thead>
              <tbody>
                {items.map(({ product, stockOwned }) => {
                  const nombre = contentFor(product.translations, "es").name;
                  const enabled = !disabled.has(product.id);
                  const base = storePriceOf(product, STORE_CURRENCY);
                  return (
                    <Tr key={product.id} className={enabled ? undefined : "opacity-60"}>
                      <Td>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={enabled}
                          aria-label={t("{{estado}}: {{name}}", {
                            estado: enabled ? t("Quitar de mi tienda") : t("Añadir a mi tienda"),
                            name: nombre,
                          })}
                          onClick={() => toggle(product.id)}
                          className={`h-6 w-11 shrink-0 rounded-full transition ${
                            enabled ? "bg-emerald-500" : "bg-ds-ink-4/40"
                          }`}
                        >
                          <span
                            className={`block h-5 w-5 transform rounded-full bg-white shadow transition ${
                              enabled ? "translate-x-5" : "translate-x-1"
                            }`}
                          />
                        </button>
                      </Td>
                      <Td className="font-medium">{nombre}</Td>
                      {/*
                        Cuánto PUBLICA de lo que tiene (Anahí, 1-sep).
                        Vacío = todo. El tope solo baja: el `max` es su
                        inventario real, y el servidor vuelve a aplicar
                        `min(tiene, tope)` porque un campo del navegador
                        no es una garantía de nada.
                      */}
                      <Td align="right">
                        <div className="flex items-center justify-end gap-2">
                          <Input
                            type="number"
                            inputMode="numeric"
                            min={0}
                            max={stockOwned}
                            placeholder={String(stockOwned)}
                            aria-label={t("Piezas que publicas de {{name}}", { name: nombre })}
                            className="w-20 text-right"
                            value={stockCaps[product.id] ?? ""}
                            onChange={(event) =>
                              setStockCaps((prev) => ({
                                ...prev,
                                [product.id]: event.target.value,
                              }))
                            }
                          />
                          <span className="whitespace-nowrap text-[12px] text-ds-ink-3">
                            {t("de {{n}}", { n: stockOwned })}
                          </span>
                        </div>
                      </Td>
                      <Td align="right" className="font-mono text-[13px] text-ds-ink-3">
                        {typeof base === "number" ? formatPrice(base, STORE_CURRENCY) : "—"}
                      </Td>
                      <Td align="right">
                        <Input
                          type="number"
                          inputMode="numeric"
                          min={1}
                          placeholder="—"
                          aria-label={t("Tu precio de {{name}}", { name: nombre })}
                          className="w-28 text-right"
                          value={ownPrices[product.id] ?? ""}
                          onChange={(event) =>
                            setOwnPrices((prev) => ({
                              ...prev,
                              [product.id]: event.target.value,
                            }))
                          }
                        />
                      </Td>
                    </Tr>
                  );
                })}
              </tbody>
            </DataTable>
          )}
        </DataTableFrame>

        {/* ── Guardar TODO — la pantalla es un solo formulario ─────────── */}
        <div className="sticky bottom-4 z-10 flex flex-wrap items-center justify-between gap-3 rounded-3xl border border-ds-border bg-white/95 p-4 shadow-ds-float backdrop-blur">
          <div className="relative min-w-0 flex-1">
            {update.isError && (
              <Alert variant="error" onDismiss={() => update.reset()}>
                {update.error instanceof Error && update.error.message
                  ? update.error.message
                  : t("No se pudo guardar. Inténtalo otra vez.")}
              </Alert>
            )}
            {saved && !update.isPending && (
              <Alert variant="success" onDismiss={() => setSaved(false)}>
                {t("Tu tienda quedó guardada.")}
              </Alert>
            )}
          </div>
          <Button className="relative" loading={update.isPending} onClick={() => void guardar()}>
            {t("Guardar cambios")}
          </Button>
        </div>
      </div>
    </>
  );
}
